import {
  MILLISECONDS_PER_DAY,
  MONGODB_ATTRIBUTES_DAYS_PAST,
} from "./keys";

// - - - - - - - - - -
// Pure week-date helpers used by per-grid week navigation (#105 phase 3).
// Extracted from app.context.js so each grid can manage its own week locally
// without touching shared state. Each helper snaps its return value to local
// midnight via `setHours(0, 0, 0, 0)` so callers can pass the result back in
// without time-of-day drift accumulating across multiple navigations. The
// +43200000 (12h) cushion absorbs DST-boundary off-by-one in either direction.
//
// Hotfix v05-04-2026-02 (ref bug: 4 reports of week-of-Monday ticking forward
// by 1 day after several next/prev cycles). The original v05-04-2026-01 build
// returned Date objects without snapping to midnight, so the +12h cushion in
// getPrevWeekMonday accumulated as time-of-day drift on every back-click —
// after two back-clicks the displayed Monday became Tuesday. The legacy
// AppContext code is unaffected because it re-parses midnight-only strings
// every navigation; the per-grid Optimizer holds a Date object in React
// state, so the drift compounds. Fix: snap to local midnight on output;
// also add the +12h cushion to getNextWeekMonday for fall-back symmetry.

const snapToLocalMidnight = (d) => {
  const result = new Date(d);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Returns the Monday of the week containing `date` as a Date object at local 00:00.
 */
export const getMondayOf = (date) => {
  const d = new Date(date);
  const dayOfWeek = d.getDay(); // sun=0, mon=1, tue=2, ...
  const diff = (1 - dayOfWeek) * MILLISECONDS_PER_DAY; // sun=+1, mon=0, tue=-1
  return snapToLocalMidnight(new Date(d.getTime() + diff));
};

/**
 * Returns the Monday of the current real-world week, at local 00:00.
 */
export const getMondayOfThisWeek = () => getMondayOf(new Date());

/**
 * Given a Monday, returns the Monday 7 days earlier at local 00:00.
 * The +43200000 (12h) avoids DST-boundary off-by-one on the subtraction;
 * the snap-to-midnight prevents time-of-day drift across repeated calls.
 * Defense in depth: input is snapped to midnight first so the cushion
 * math behaves correctly regardless of caller-supplied time-of-day.
 */
export const getPrevWeekMonday = (currentMonday) => {
  const startOfDay = snapToLocalMidnight(new Date(currentMonday));
  const ms = startOfDay.getTime() - MILLISECONDS_PER_DAY * 7 + 43200000;
  return snapToLocalMidnight(new Date(ms));
};

/**
 * Given a Monday, returns the Monday 7 days later at local 00:00.
 * The +43200000 (12h) avoids DST-boundary off-by-one on the addition
 * (specifically across the fall-back boundary in November); the
 * snap-to-midnight prevents time-of-day drift across repeated calls.
 * Defense in depth: input is snapped to midnight first so the cushion
 * math behaves correctly regardless of caller-supplied time-of-day.
 */
export const getNextWeekMonday = (currentMonday) => {
  const startOfDay = snapToLocalMidnight(new Date(currentMonday));
  const ms = startOfDay.getTime() + MILLISECONDS_PER_DAY * 7 + 43200000;
  return snapToLocalMidnight(new Date(ms));
};

/**
 * Builds the 7-element week array (en-US locale strings, mon..sun) from a
 * Monday Date. Mirrors AppContext.createWeekDatesArray exactly.
 */
export const buildWeekDatesArray = (mondayDate) => {
  const ms = new Date(mondayDate).getTime();
  return [0, 1, 2, 3, 4, 5, 6].map((offset) =>
    new Date(ms + MILLISECONDS_PER_DAY * offset).toLocaleDateString("en-us")
  );
};

/**
 * True if navigating to `targetMondayDate` would go beyond the historical
 * data limit (MONGODB_ATTRIBUTES_DAYS_PAST days before today).
 */
export const isWeekBeyondLimit = (targetMondayDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoffDate = new Date(
    today.getTime() - MONGODB_ATTRIBUTES_DAYS_PAST * MILLISECONDS_PER_DAY
  );
  cutoffDate.setHours(0, 0, 0, 0);
  return new Date(targetMondayDate) < cutoffDate;
};

/**
 * True if navigating one week back from `currentWeekDatesArray` is allowed.
 */
export const canNavigateWeekBackFrom = (currentWeekDatesArray) => {
  if (!currentWeekDatesArray || currentWeekDatesArray.length !== 7) return false;
  const targetMonday = getPrevWeekMonday(currentWeekDatesArray[0]);
  return !isWeekBeyondLimit(targetMonday);
};
